import React, { forwardRef, useImperativeHandle } from 'react';
import { playerWindowGeometry } from './playerWindowGeometry';
import { useWindowGeometry, type ToolWindowHandle } from './useWindowGeometry';
import { TomePanel, TomeTitleBlock, type TomeVibe, type TomeVibeName } from '../primitives/tome';
import './TomeWindow.css';

export interface TomeWindowProps {
  /** Unique ID for persisting position/size */
  id: string;
  /** Window title shown in header */
  title: string;
  /** Eyebrow text above the title */
  eyebrow?: string;
  /** Control visibility */
  isOpen: boolean;
  /** Called when window is closed (X button) */
  onClose: () => void;
  children: React.ReactNode;
  /** Initial position */
  defaultPosition?: { x: number; y: number };
  /** Initial size */
  defaultSize?: { width: number; height: number };
  /** Minimum window size */
  minSize?: { width: number; height: number };
  /** Maximum window size */
  maxSize?: { width: number; height: number };
  /** Allow resize */
  resizable?: boolean;
  /** Optional manual zIndex override */
  zIndex?: number;
  /** Show on top when dragging */
  bringToFrontOnDrag?: boolean;
  /** Custom class name */
  className?: string;
  /** Disable localStorage geometry */
  persistState?: boolean;
  /** Launch expanded even when saved chrome state was collapsed/minimized */
  restoreOnOpen?: boolean;
  /** Stable selector for tests and VQA */
  testId?: string;
  /** Tome vibe preset */
  vibe?: TomeVibeName | TomeVibe;
  /** Whether the children are enclosed in the standard scroll container (default true) */
  scrollable?: boolean;
}

const DEFAULT_MIN_SIZE = { width: 320, height: 200 };
const DEFAULT_MAX_SIZE = { width: 1400, height: 1000 };

export const TomeWindow = forwardRef<ToolWindowHandle, TomeWindowProps>(({
  id,
  title,
  eyebrow,
  isOpen,
  onClose,
  children,
  defaultPosition,
  defaultSize = { width: 680, height: 580 },
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  resizable = true,
  zIndex: initialZIndex = 100,
  bringToFrontOnDrag = true,
  className = '',
  persistState = true,
  restoreOnOpen = false,
  testId,
  vibe,
  scrollable = true,
}, forwardedRef) => {
  const {
    state,
    isDefaultGeometry,
    isDragging,
    zIndex,
    windowRef,
    startDrag,
    startResize,
    handle,
  } = useWindowGeometry({
    id,
    defaultPosition,
    defaultSize,
    minSize,
    maxSize,
    persistState,
    restoreOnOpen,
    isPlayerWindow: true,
    bringToFrontOnDrag,
    initialZIndex,
  });

  useImperativeHandle(forwardedRef, () => handle, [handle]);

  if (!isOpen) return null;

  return (
    <div
      ref={windowRef}
      data-testid={testId}
      className={`tome-window ${isDragging ? 'tome-window-dragging' : ''} ${className}`}
      style={{
        position: 'absolute',
        left: state.x,
        top: state.y,
        width: state.width,
        height: state.height,
        ...playerWindowGeometry(state, minSize),
        zIndex,
        pointerEvents: 'auto',
      }}
    >
      <TomePanel
        vibe={vibe}
        shapeSeed={id}
        scrollable={scrollable}
        className="tome-window-panel"
        header={
          <div
            className="tome-window-drag-bar"
            onMouseDown={startDrag}
          >
            <TomeTitleBlock
              eyebrow={eyebrow}
              title={title}
              onClose={onClose}
              closeLabel={`Close ${title}`}
              actions={isDefaultGeometry ? undefined : <button type="button" className="tome-window-reset"
                aria-label={`Reset ${title} size and place`} title="Reset size and place"
                onClick={handle.resetGeometry}>⟲</button>}
            />
          </div>
        }
      >
        {children}
      </TomePanel>

      {/* Resize Handle */}
      {resizable && (
        <div
          className="tome-window-resize-handle"
          onMouseDown={startResize}
          title="Drag to resize"
          aria-hidden="true"
        />
      )}
    </div>
  );
});

TomeWindow.displayName = 'TomeWindow';
